import { Link, useParams } from "react-router-dom";
import { MessageCircle, ArrowRight, Sparkles, Globe2, Star, ShieldCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import MakeoverEnquiryForm from "../components/MakeoverEnquiryForm";
import MakeoverExpertCard from "../components/MakeoverExpertCard";
import MakeoverPremiumSections from "../components/MakeoverPremiumSections";
import {
    MAKEOVER_MASTER_PATH, MAKEOVER_COUNTRIES, MAKEOVER_CATEGORIES,
    MAKEOVER_FAQS, makeoverWhatsAppUrl, MAKEOVER_EXPERT,
} from "../data/makeoverData";
import { useSettings } from "../context/SettingsContext";
import { ShareMenu } from "../components/timeConverter/ShareMenu";

const SITE_URL = "https://anandtutorials.com";
// V82 — All WhatsApp CTAs on the makeover pages open Ishpreet Kaur's number
// (the makeover expert). Falls back to her default if the admin hasn't
// customised the setting. Sanitised to digits-only for wa.me links.
const MAKEOVER_WA_FALLBACK = "918085796268";

// Helper: settings toggle is ON unless explicitly stored as false.
// Undefined / missing / true → visible (safe default).
const isOn = (v) => v !== false;

/**
 * V77 — Single component rendering all makeover pages:
 *   • variant="master"   → /online-makeover-classes
 *   • variant="country"  → /online-makeover-classes-<country>
 *   • variant="category" → /online-makeover-classes/<category>
 *
 * V80 — Every major section is wrapped in an admin visibility toggle
 * (read from GET /api/settings/public). JSON-LD schema graph now includes
 * Organization + WebPage + BreadcrumbList + Service + Course + Person +
 * FAQPage + AggregateRating (attributed to the verified Google Business
 * Profile) for maximum SEO/GEO coverage across Google AI, Bing, Perplexity.
 */
export default function MakeoverLanding({ variant = "master", countrySlug, categorySlug }) {
    const { settings } = useSettings();
    const params = useParams();
    // V82 — Hero WhatsApp CTA opens Ishpreet Kaur's number, not the general
    // Anand Tutorials line. Digits-only for wa.me compatibility.
    const whatsapp = (settings?.makeover_expert_whatsapp || MAKEOVER_WA_FALLBACK).replace(/[^0-9]/g, "");

    // V80 — Section visibility flags (default ON if setting is undefined)
    const showHero = isOn(settings?.makeover_show_hero);
    const showExpert = isOn(settings?.makeover_show_expert);
    const showPillars = isOn(settings?.makeover_show_pillars);
    const showCategories = isOn(settings?.makeover_show_categories);
    const showCountries = isOn(settings?.makeover_show_countries);
    const showPremium = isOn(settings?.makeover_show_why_learn) || isOn(settings?.makeover_show_why_choose) || isOn(settings?.makeover_show_trust) || isOn(settings?.makeover_show_testimonials);
    const showFaq = isOn(settings?.makeover_show_faq);
    const showEnquiry = isOn(settings?.makeover_show_enquiry);

    let country = null, category = null;
    if (variant === "country") country = MAKEOVER_COUNTRIES.find((c) => c.slug === (countrySlug || params.countrySlug));
    if (variant === "category") category = MAKEOVER_CATEGORIES.find((c) => c.slug === (categorySlug || params.categorySlug));

    // ---- Compute per-variant metadata ----
    const path = variant === "country" && country ? `${MAKEOVER_MASTER_PATH}-${country.slug}`
        : variant === "category" && category ? `${MAKEOVER_MASTER_PATH}/${category.slug}`
        : MAKEOVER_MASTER_PATH;
    // V174 — Makeup-first master metadata. Country/category variants keep their
    // existing makeover identity so no unrelated page is disturbed. The master
    // page now leads with the primary keyword "Makeup Classes" and principal
    // supporting keyword "Online Makeup Classes" — personal grooming / styling
    // / image consulting are kept as supporting topics (secondary intent).
    const shareKey = variant === "country" ? country?.slug : variant === "category" ? category?.slug : "";
    const shareUrl = `${SITE_URL}/api/share/makeover${shareKey ? `?page=${shareKey}` : ""}`;
    const eyebrow = variant === "country" ? `${country?.flag} ${country?.name} · ${country?.region}`
        : variant === "category" ? category?.tagline
        : "Live · 1-on-1 · Worldwide";
    const h1 = variant === "country" ? `1-on-1 Live Online Makeover Classes in ${country?.name}`
        : variant === "category" ? `${category?.name} — 1-on-1 Live Online Classes`
        : "Online Makeup Classes — One-on-One Makeup Lessons & Training";
    const subTitle = variant === "country"
        ? `Personalised makeover coaching for clients across ${country?.name}. Live 1-on-1 sessions scheduled for your local ${country?.tz}. Grooming, makeup, styling, image consulting and confidence coaching — every session tailored to YOUR face, wardrobe and goals.`
        : variant === "category"
        ? `${category?.description} Delivered live 1-on-1 over Zoom or Google Meet. Worldwide coverage — USA, UK, UAE, Canada, Australia, Singapore, and more. Session recordings shared for revision.`
        : "Live online makeup classes from Anand Tutorials — one-on-one makeup lessons with a personal instructor, delivered over Zoom to learners in India and worldwide. Learn everyday makeup, foundation and eye makeup techniques, self-makeup for daily use, plus personal grooming, styling and image consulting as supporting topics. Every session is you + your coach on camera — no batches, no pre-recorded videos.";
    const seoTitle = variant === "country" ? `1-on-1 Live Online Makeover Classes in ${country?.name} | Anand Tutorials`
        : variant === "category" ? `${category?.name} — Live 1-on-1 Online Classes Worldwide | Anand Tutorials`
        : "Online Makeup Classes & Private Makeup Lessons | Anand Tutorials";
    const seoDescription = variant === "country"
        ? `Book 1-on-1 live online makeover classes from ${country?.name}. Personal grooming, makeup training, styling, image consulting and confidence coaching — scheduled for your local ${country?.tz}. Certified coaches. Session recording included.`
        : variant === "category"
        ? `${category?.name} 1-on-1 live online classes at Anand Tutorials. ${category?.tagline}. Worldwide coverage, tutor-matched to your timezone, session recording included.`
        : "Explore live online makeup classes and personalised one-on-one lessons with Anand Tutorials. Learn makeup techniques through individual online guidance.";
    const keywords = variant === "master" || (variant !== "country" && variant !== "category")
        ? [
            // Primary
            "makeup classes",
            "online makeup classes",
            // Principal supporting
            "online makeup lessons",
            "private makeup lessons",
            "one-on-one makeup classes",
            "one-on-one online makeup classes",
            "1-on-1 makeup classes online",
            "live online makeup classes",
            "live online makeup lessons",
            "makeup tutor online",
            "personal makeup tutor online",
            "online makeup instructor",
            "learn makeup online",
            "online makeup classes for beginners",
            // V174.1 — Training / course / coaching cluster
            "makeup training",
            "online makeup training",
            "one-on-one makeup training online",
            "makeup courses",
            "online makeup courses",
            "makeup coaching",
            "personalized makeup training online",
            "virtual one-on-one makeup lessons",
            // Technique coverage
            "everyday makeup lessons",
            "natural makeup techniques",
            "self makeup classes",
            "learn self makeup online",
            "foundation application lessons",
            "eye makeup lessons",
            "step-by-step makeup lessons",
            // Secondary supporting topics
            "personal grooming classes",
            "online makeover classes",
            "personal styling classes",
            "image consulting online",
            // Booking / international intent
            "book online makeup classes",
            "worldwide online makeup lessons",
            "online makeup classes India",
            "online makeup classes USA",
            "online makeup classes UK",
            "online makeup training Canada",
            "online makeup courses Australia",
            "online makeup classes UAE",
          ].join(", ")
        : [
            "1-on-1 live online makeover classes",
            "online personal styling classes",
            "live image consulting",
            "online grooming coach",
            "online makeup classes",
            "online personal branding coach",
            "online styling expert",
            "personal grooming classes",
            "virtual makeover coaching",
            "one-on-one online styling",
            "live confidence coaching",
            "worldwide online makeover classes",
            "online makeover classes India",
            variant === "country" ? `online makeover classes ${country?.name}` : "",
            variant === "category" ? `${category?.name?.toLowerCase()} online classes` : "",
          ].filter(Boolean).join(", ");

    // ---- V80 · Advanced JSON-LD graph (maximum SEO/GEO surface) ----
    const canonicalUrl = `${SITE_URL}${path}`;
    const expertPhone = settings?.makeover_expert_phone || MAKEOVER_EXPERT.defaults.phone;
    const expertInstagram = settings?.makeover_expert_instagram_url || MAKEOVER_EXPERT.defaults.instagramUrl;
    const expertGoogleMaps = settings?.makeover_expert_google_maps_url || MAKEOVER_EXPERT.defaults.googleMapsUrl;
    const ratingValue = parseFloat(settings?.makeover_aggregate_rating || "4.7");
    const reviewCount = parseInt(settings?.makeover_review_count || 0, 10);
    // AggregateRating is emitted ONLY when a positive review count is
    // configured — Google flags fabricated / empty counts. Attribution is
    // strictly to Ishpreet Kaur's verified Google Business Profile URL.
    const aggregateRating = reviewCount > 0 && ratingValue > 0 ? {
        "@type": "AggregateRating",
        "ratingValue": ratingValue.toFixed(1),
        "reviewCount": reviewCount,
        "bestRating": "5",
        "worstRating": "1",
        "url": expertGoogleMaps,
    } : null;

    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": `${SITE_URL}/#organization`,
        "name": "Anand Tutorials",
        "url": SITE_URL,
        "logo": `${SITE_URL}/logo-anand-tutorials.png`,
        "sameAs": [
            settings?.facebook_url,
            settings?.instagram_url,
            settings?.linkedin_url,
            settings?.youtube_url,
            settings?.twitter_url,
        ].filter(Boolean),
        "contactPoint": [{
            "@type": "ContactPoint",
            "contactType": "customer service",
            "telephone": settings?.head_office_phone || "+91-8085796268",
            "email": settings?.head_office_email || "anandhometutorials@gmail.com",
            "availableLanguage": ["English", "Hindi"],
            "areaServed": ["IN", "US", "GB", "AE", "CA", "AU", "SG", "MY", "SA", "QA", "KW", "OM", "BH", "NZ"],
        }],
    };

    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        "url": canonicalUrl,
        "name": seoTitle,
        "description": seoDescription,
        "isPartOf": { "@id": `${SITE_URL}/#organization` },
        "inLanguage": "en",
        "primaryImageOfPage": {
            "@type": "ImageObject",
            "url": `${SITE_URL}${MAKEOVER_EXPERT.image}`,
        },
        "breadcrumb": { "@id": `${canonicalUrl}#breadcrumb` },
    };

    const breadcrumbItems = [{ name: "Makeover Classes", to: MAKEOVER_MASTER_PATH }];
    if (variant === "country") breadcrumbItems.push({ name: country?.name });
    if (variant === "category") breadcrumbItems.push({ name: category?.name });

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Makeover Classes", item: `${SITE_URL}${MAKEOVER_MASTER_PATH}` },
            ...(variant === "country" && country ? [{ "@type": "ListItem", position: 3, name: country.name, item: canonicalUrl }] : []),
            ...(variant === "category" && category ? [{ "@type": "ListItem", position: 3, name: category.name, item: canonicalUrl }] : []),
        ],
    };

    // V174 — Makeup-first keywords applied to Service + Course schema on the
    // master page so Google / Bing / AI search engines see the primary intent
    // explicitly declared alongside the existing makeover coverage.
    const isMaster = variant !== "country" && variant !== "category";
    const makeupKeywordsList = isMaster ? [
        "Online Makeup Classes",
        "Makeup Classes",
        "Private Makeup Lessons",
        "One-on-One Makeup Classes",
        "One-on-One Online Makeup Classes",
        "Live Online Makeup Lessons",
        "Makeup Tutor Online",
        "Personal Makeup Tutor Online",
        "Learn Makeup Online",
        "Makeup Training",
        "Online Makeup Training",
        "One-on-One Makeup Training Online",
        "Makeup Courses",
        "Online Makeup Courses",
        "Makeup Coaching",
        "Everyday Makeup Techniques",
        "Foundation Application",
        "Eye Makeup Lessons",
        "Self Makeup Classes",
        "Personal Grooming Classes",
        "Online Makeover Classes",
    ] : [];
    const makeupAboutList = isMaster ? [
        { "@type": "Thing", "name": "Online Makeup Classes" },
        { "@type": "Thing", "name": "Online Makeup Training" },
        { "@type": "Thing", "name": "Private Makeup Lessons" },
        { "@type": "Thing", "name": "One-on-One Makeup Instruction" },
        { "@type": "Thing", "name": "Makeup Coaching" },
        { "@type": "Thing", "name": "Everyday Makeup Techniques" },
        { "@type": "Thing", "name": "Personal Grooming" },
    ] : [];

    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonicalUrl}#service`,
        "name": h1,
        ...(isMaster ? { "alternateName": ["Online Makeup Classes", "Private Makeup Lessons", "One-on-One Makeup Lessons Online", "Online Makeup Training", "Online Makeup Courses"] } : {}),
        "description": subTitle,
        "serviceType": category?.name || (isMaster ? "Live Online Makeup Classes & Training" : "Live Online Makeover Coaching"),
        "provider": { "@type": "EducationalOrganization", "@id": `${SITE_URL}/#organization`, "name": "Anand Tutorials", "url": SITE_URL },
        "areaServed": variant === "country" && country ? { "@type": "Country", "name": country.name } : { "@type": "Place", "name": "Worldwide + Pan India" },
        "availableChannel": { "@type": "ServiceChannel", "serviceUrl": canonicalUrl, "availableLanguage": ["English"] },
        "audience": { "@type": "Audience", "audienceType": isMaster ? "Learners worldwide seeking live one-on-one makeup lessons, personal grooming and styling guidance" : "Adults seeking personal grooming, styling, image consulting and confidence coaching" },
        "offers": { "@type": "Offer", "priceCurrency": country?.currency || "USD", "url": canonicalUrl, "availability": "https://schema.org/InStock" },
        ...(makeupKeywordsList.length ? { "keywords": makeupKeywordsList.join(", ") } : {}),
        ...(makeupAboutList.length ? { "about": makeupAboutList } : {}),
        ...(aggregateRating ? { "aggregateRating": aggregateRating } : {}),
    };

    const personSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `${SITE_URL}${MAKEOVER_MASTER_PATH}#expert-ishpreet-kaur`,
        "name": MAKEOVER_EXPERT.name,
        "givenName": "Ishpreet",
        "familyName": "Kaur",
        "jobTitle": MAKEOVER_EXPERT.title,
        "description": MAKEOVER_EXPERT.bioParagraphs?.[0],
        "image": `${SITE_URL}${MAKEOVER_EXPERT.image}`,
        "telephone": expertPhone,
        "url": `${SITE_URL}${MAKEOVER_MASTER_PATH}#meet-your-makeover-expert`,
        "sameAs": [expertInstagram, expertGoogleMaps].filter(Boolean),
        "worksFor": { "@type": "EducationalOrganization", "@id": `${SITE_URL}/#organization`, "name": "Anand Tutorials" },
        "areaServed": { "@type": "Place", "name": "Worldwide + Pan India" },
        "knowsAbout": MAKEOVER_CATEGORIES.map((c) => c.name),
        "knowsLanguage": ["English", "Hindi", "Punjabi"],
        ...(aggregateRating ? { "aggregateRating": aggregateRating } : {}),
    };

    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "@id": `${canonicalUrl}#course`,
        "name": h1,
        ...(isMaster ? { "alternateName": ["Online Makeup Classes", "Private Makeup Lessons", "One-on-One Makeup Lessons", "Online Makeup Training", "Online Makeup Courses"] } : {}),
        "description": subTitle,
        "provider": { "@type": "EducationalOrganization", "@id": `${SITE_URL}/#organization`, "name": "Anand Tutorials", "url": SITE_URL },
        "instructor": { "@id": `${SITE_URL}${MAKEOVER_MASTER_PATH}#expert-ishpreet-kaur` },
        "educationalLevel": "Beginner to Advanced",
        "inLanguage": "en",
        "hasCourseInstance": [{
            "@type": "CourseInstance",
            "courseMode": "Online",
            "courseWorkload": "PT1H",
            "location": { "@type": "VirtualLocation", "url": canonicalUrl },
            "instructor": { "@id": `${SITE_URL}${MAKEOVER_MASTER_PATH}#expert-ishpreet-kaur` },
        }],
        "offers": { "@type": "Offer", "category": "Live 1-on-1 Coaching", "priceCurrency": country?.currency || "USD", "availability": "https://schema.org/InStock", "url": canonicalUrl },
        ...(makeupKeywordsList.length ? { "keywords": makeupKeywordsList.join(", ") } : {}),
        ...(makeupAboutList.length ? { "about": makeupAboutList } : {}),
        ...(aggregateRating ? { "aggregateRating": aggregateRating } : {}),
    };

    const faqSchema = showFaq ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${canonicalUrl}#faq`,
        "mainEntity": MAKEOVER_FAQS.map(([q, a]) => ({ "@type": "Question", "name": q, "acceptedAnswer": { "@type": "Answer", "text": a } })),
    } : null;

    // Compose the JSON-LD graph — pass all schemas via the `schemas` array
    // so <SEO> emits them in one Helmet block for maximum consistency.
    const schemaGraph = [
        organizationSchema,
        webPageSchema,
        breadcrumbSchema,
        serviceSchema,
        courseSchema,
        personSchema,
        faqSchema,
    ].filter(Boolean);

    return (
        <div data-testid={`makeover-page-${variant}`} className="bg-white">
            <SEO title={seoTitle} description={seoDescription} keywords={keywords} path={path} schemas={schemaGraph} />

            <Breadcrumbs items={breadcrumbItems} />

            {showHero && (
                <PageHeader eyebrow={eyebrow} title={h1} subtitle={subTitle}>
                    <div className="flex flex-wrap gap-3">
                        <a href="#enquire" data-testid="makeover-cta-book" className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-bold px-5 py-3 rounded-lg">
                            Book a 1-on-1 slot <ArrowRight className="w-4 h-4" />
                        </a>
                        <a href={makeoverWhatsAppUrl(whatsapp)} target="_blank" rel="noopener noreferrer" data-smart-contact-skip="true" data-testid="makeover-cta-whatsapp" className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold px-5 py-3 rounded-lg">
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                        </a>
                        <ShareMenu label="Share" testId="makeover-share" className="!h-auto py-3 !text-base"
                            url={shareUrl} title={seoTitle}
                            message={`Take a look at Anand Tutorials' 1:1 Live Online Makeover Classes with Ishpreet Kaur:\n${shareUrl}`} />
                    </div>
                </PageHeader>
            )}

            {showExpert && (
                <MakeoverExpertCard variant="full" priority={variant === "master"} />
            )}

            {/* V174 — Makeup-first educational content (master page only).
                Provides original, helpful copy for Google / Bing / AI engines
                targeting "Online Makeup Classes" + "Private Makeup Lessons".
                Safe additive section — nothing above or below is modified. */}
            {isMaster && (
                <section data-testid="makeover-section-makeup-content" className="py-14 bg-white">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-3 flex items-center gap-2"><Sparkles className="w-3 h-3" /> About these classes</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6 max-w-3xl">What our online makeup classes cover</h2>
                        <div className="grid md:grid-cols-2 gap-6 text-slate-700 text-[15px] leading-relaxed">
                            <div className="space-y-5">
                                <p>
                                    <strong className="text-slate-900">Online makeup classes</strong> at Anand Tutorials are
                                    live, one-on-one lessons delivered over Zoom or Google Meet by{" "}
                                    <Link to={`${MAKEOVER_MASTER_PATH}#meet-your-makeover-expert`} className="text-rose-600 hover:underline">Ishpreet Kaur — our 1-on-1 Live Online Makeover Expert</Link>.
                                    Your camera is on; your coach watches you apply makeup in real time, corrects
                                    your technique and tailors every session to <em>your</em> face, skin tone and goals.
                                </p>
                                <h3 className="font-heading font-bold text-slate-900 text-lg">Live one-on-one makeup lessons</h3>
                                <p>
                                    Every class is strictly 1 tutor : 1 student. Nothing is pre-recorded — the
                                    structure of each session is decided in the first ten minutes based on where
                                    you are and what you want to walk away able to do yourself. The session
                                    recording is shared with you afterwards for private revision.
                                </p>
                                <h3 className="font-heading font-bold text-slate-900 text-lg">Class, course, training or private lesson — what's the difference?</h3>
                                <p>
                                    In everyday use the terms overlap, so here is how we use them. A
                                    <strong> makeup class</strong> is one live session on a specific topic.
                                    <strong> Online makeup training</strong> is a longer, structured sequence of
                                    classes designed to build a complete skill set at your own pace.
                                    A <strong>makeup course</strong> is a fully planned curriculum with a defined start
                                    and end. <strong>Private makeup lessons</strong> describe any of the above delivered strictly
                                    one-on-one, which is the only format we offer. The right fit is agreed with
                                    you during enquiry based on your goal and the time you can commit — we do
                                    not sell generic bundles.
                                </p>
                                <h3 className="font-heading font-bold text-slate-900 text-lg">What a typical learner covers</h3>
                                <ul className="list-disc pl-6 space-y-1.5 marker:text-rose-500">
                                    <li>Skin preparation, priming and getting the base right</li>
                                    <li>Foundation matching and natural, even application</li>
                                    <li>Concealer, contour and highlighter basics</li>
                                    <li>Eye makeup — eyeshadow, eyeliner, mascara, brows</li>
                                    <li>Blush, lips and finishing a complete everyday look</li>
                                    <li>Occasion looks — office, evening, sangeet and bridal fundamentals (introduced once your base routine is confident)</li>
                                </ul>
                            </div>
                            <div className="space-y-5">
                                <h3 className="font-heading font-bold text-slate-900 text-lg">Who private makeup lessons suit</h3>
                                <p>
                                    Complete beginners wanting a reliable everyday routine, hobbyists refining
                                    technique, brides-to-be learning their own bridal basics, and professionals
                                    who want to look consistent on camera — the pace adjusts to you. Men and
                                    women are both welcome; let us know your preference during enquiry and we match
                                    accordingly.
                                </p>
                                <h3 className="font-heading font-bold text-slate-900 text-lg">Personal grooming, styling &amp; image consulting</h3>
                                <p>
                                    Learners who want more than makeup can add{" "}
                                    <Link to={`${MAKEOVER_MASTER_PATH}/personal-grooming`} className="text-rose-600 hover:underline">personal grooming</Link>,{" "}
                                    <Link to={`${MAKEOVER_MASTER_PATH}/personal-styling`} className="text-rose-600 hover:underline">personal styling</Link>,{" "}
                                    <Link to={`${MAKEOVER_MASTER_PATH}/skincare-guidance`} className="text-rose-600 hover:underline">skincare guidance</Link>,{" "}
                                    <Link to={`${MAKEOVER_MASTER_PATH}/hair-styling-guidance`} className="text-rose-600 hover:underline">hair styling guidance</Link> and{" "}
                                    <Link to={`${MAKEOVER_MASTER_PATH}/image-consulting`} className="text-rose-600 hover:underline">image consulting</Link>{" "}
                                    as supporting 1-on-1 tracks. Skincare and hair styling guidance here is educational
                                    — not a medical consultation or treatment.
                                </p>
                                <h3 className="font-heading font-bold text-slate-900 text-lg">International delivery &amp; time zones</h3>
                                <p>
                                    Classes run worldwide — learners in India, the USA, UK, Canada, Australia,
                                    UAE, Saudi Arabia, Singapore, Malaysia, New Zealand and across Europe already
                                    attend sessions scheduled for their local evenings and weekends. During
                                    enquiry you share your city and timezone and we match a slot you can
                                    comfortably attend.
                                </p>
                                <h3 className="font-heading font-bold text-slate-900 text-lg">How to enquire about fees &amp; availability</h3>
                                <p>
                                    Fees depend on session length, chosen category and whether you prefer a
                                    single trial, a short package or ongoing coaching. Use the <a href="#enquire" className="text-rose-600 hover:underline">enquiry form on this page</a> or
                                    tap the WhatsApp button — we respond with available slots and a tailored
                                    quote the same day. Nothing is auto-charged.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {showPillars && (
                <section data-testid="makeover-section-pillars" className="py-14 bg-white">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-3 flex items-center gap-2"><Sparkles className="w-3 h-3" /> Why us</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8 max-w-3xl">Live personalised coaching — nothing pre-recorded.</h2>
                        <div className="grid md:grid-cols-3 gap-4">
                            {[
                                { t: "1-on-1 live sessions", d: "Every session is 1 tutor : 1 student. Two-way HD video. Camera-based practice on YOUR face, wardrobe or hair." },
                                { t: "Certified coaches", d: "Image consultants, professional makeup artists, hairstylists and personality-development coaches — vetted through our 4-step verification." },
                                { t: "Your timezone, your goals", d: "Scheduled for your local evening / weekend. Recording of every session shared with you for private revision." },
                            ].map((p, i) => (
                                <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                    <div className="w-9 h-9 rounded-lg bg-rose-600 text-white flex items-center justify-center font-heading font-bold text-sm mb-3">{i + 1}</div>
                                    <h3 className="font-heading font-semibold text-slate-900 mb-1">{p.t}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">{p.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {showCategories && (
                <section id="categories" data-testid="makeover-section-categories" className="py-14 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-3 flex items-center gap-2"><Star className="w-3 h-3" /> Service categories</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8 max-w-3xl">Ten 1-on-1 coaching tracks, tailored to you.</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="makeover-category-grid">
                            {MAKEOVER_CATEGORIES.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`${MAKEOVER_MASTER_PATH}/${c.slug}`}
                                    data-testid={`makeover-cat-${c.slug}`}
                                    className={`block bg-white border border-slate-200 rounded-2xl p-5 hover:border-rose-300 hover:shadow-md transition-all ${category?.slug === c.slug ? "ring-2 ring-rose-500" : ""}`}
                                >
                                    <h3 className="font-heading font-bold text-slate-900 mb-1">{c.name}</h3>
                                    <div className="text-[11px] text-rose-600 font-semibold uppercase tracking-wider mb-2">{c.tagline}</div>
                                    <p className="text-sm text-slate-600 leading-relaxed">{c.description}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {showCountries && (
                <section data-testid="makeover-section-countries" className="py-14 bg-white">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-3 flex items-center gap-2"><Globe2 className="w-3 h-3" /> Worldwide + Pan India</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8 max-w-3xl">Serving clients in 15 markets.</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="makeover-country-grid">
                            {MAKEOVER_COUNTRIES.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`${MAKEOVER_MASTER_PATH}-${c.slug}`}
                                    data-testid={`makeover-country-${c.slug}`}
                                    className={`flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-rose-300 p-3 transition-all ${country?.slug === c.slug ? "ring-2 ring-rose-500 bg-white" : ""}`}
                                >
                                    <span className="text-lg">{c.flag}</span>
                                    <div className="min-w-0">
                                        <div className="font-heading font-semibold text-slate-900 text-sm truncate">{c.name}</div>
                                        <div className="text-[10px] text-slate-500 truncate">{c.tz}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {showPremium && (
                <MakeoverPremiumSections
                    showWhyLearn={isOn(settings?.makeover_show_why_learn)}
                    showWhyChoose={isOn(settings?.makeover_show_why_choose)}
                    showTrust={isOn(settings?.makeover_show_trust)}
                    showTestimonials={isOn(settings?.makeover_show_testimonials)}
                />
            )}

            {(showFaq || showEnquiry) && (
                <section id="enquire" data-testid="makeover-section-enquire" className="py-14 bg-slate-50">
                    <div className={`max-w-6xl mx-auto px-6 lg:px-8 grid gap-8 ${showFaq && showEnquiry ? "lg:grid-cols-5" : "lg:grid-cols-1"}`}>
                        {showFaq && (
                            <div className={showEnquiry ? "lg:col-span-3" : ""}>
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-3">FAQ</div>
                                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Common questions.</h2>
                                <div className="space-y-2">
                                    {MAKEOVER_FAQS.map(([q, a], i) => (
                                        <details key={q} data-testid={`makeover-faq-${i}`} className="group bg-white border border-slate-200 rounded-xl p-4 [&_summary::-webkit-details-marker]:hidden">
                                            <summary className="cursor-pointer font-heading font-semibold text-slate-900 flex items-center justify-between gap-3">
                                                {q}
                                                <span className="text-rose-600 text-lg group-open:rotate-45 transition-transform">+</span>
                                            </summary>
                                            <div className="text-sm text-slate-600 mt-3 leading-relaxed">{a}</div>
                                        </details>
                                    ))}
                                </div>
                                <div className="mt-8 p-4 rounded-xl bg-white border border-slate-200 flex items-start gap-3">
                                    <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                                    <div className="text-xs text-slate-700 leading-relaxed">
                                        <strong className="text-slate-900">Live 1-on-1 only.</strong> No batches, no pre-recorded courses. Every session is you + your assigned coach on Zoom or Google Meet. Session recordings are private and shared with you for revision.
                                    </div>
                                </div>
                            </div>
                        )}
                        {showEnquiry && (
                            <div className={showFaq ? "lg:col-span-2" : ""}>
                                <div className="sticky top-24">
                                    <MakeoverEnquiryForm
                                        defaultCountry={country?.name || ""}
                                        defaultService={category?.name || ""}
                                        source={`makeover-${variant}${country ? `-${country.slug}` : ""}${category ? `-${category.slug}` : ""}`}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
}
